import React, {useCallback, useMemo, useState} from "react";
import CrudModule, {ItemField, OnSubmitDone} from "../../../Crudder/CrudModule";
import {CurrencyResponse} from "../../../Api/data/currency/types";
import {getAllCurrencies} from "../../../Api/data/currency/client";
import {useKeycloak} from "@react-keycloak/web";
import {InvoiceRequest, InvoiceResponse, InvoiceState, PaymentMethod} from "../../../Api/data/invoice/types";
import {
    addInvoice,
    deleteInvoice,
    getAllInvoices,
    getInitialData,
    getInvoiceDetails,
    updateInvoice
} from "../../../Api/data/invoice/client";
import {Box, Grid, MenuItem, Chip, Link as MuiLink} from "@material-ui/core";
import {Link} from "react-router-dom";
import InputSelect from "../../../Crudder/components/InputSelect";
import {getAllCompanies} from "../../../Api/data/company/client";
import {CompanyResponse} from "../../../Api/data/company/types";
import {formatNumber, formatNumberThousands} from "../../../../utils/Date";
import {dateFormatter, normalizeInvoiceStateLabel, normalizeStateLabel} from "../../../Crudder/utils/utils";
import createDecorator from "final-form-calculate";
import {RealizationPlace} from "../../../Api/data/offer/types";
import InvoiceItemsFormPart from "./components/InvoiceItemsFormPart";
import InvoiceForm from "./components/InvoiceForm";
import { useLocation } from "react-router";
import { round } from "lodash";
import moment from "moment";
import {Switches} from "mui-rff";

// Function to calculate payment status based on invoice state and due date
const getPaymentStatus = (invoiceState: InvoiceState, dueDate: Date | undefined) => {
    if (invoiceState === InvoiceState.Paid) {
        return "zaplaceno";
    }

    if (!dueDate) {
        return "k zaplacení";
    }

    // Get current date/time in local timezone
    const today = new Date();

    // Round due date up to end of that day in local timezone
    const due = new Date(dueDate);
    due.setHours(23, 59, 59, 999);

    if (due < today) {
        return "po splatnosti";
    }

    return "k zaplacení";
};

// Function to get payment status color
const getPaymentStatusColor = (status: string) => {
    switch (status) {
        case "zaplaceno":
            return "#4caf50"; // green
        case "k zaplacení":
            return "#ff9800"; // orange
        case "po splatnosti":
            return "#f44336"; // red
        default:
            return "#9e9e9e"; // grey
    }
};

const decorateDates = (value, name, allValues: any) => {
    let due = 14; // days

    if (allValues?.company?.invoiceDue) {
        due = allValues.company?.invoiceDue;
    }

    if (!allValues?.issueDate) {
        return {};
    }

    const date = moment(allValues?.issueDate).toDate();
    const calculatedDueDate = date ? new Date(date.getTime() + due * 24 * 60 * 60 * 1000) : undefined;

    const updates: any = {};

    // Only set dueDate if it's not already set from API
    if (!allValues?.dueDate) {
        updates.dueDate = calculatedDueDate;
    }

    // Only set taxableTransactionDate if it's not already set from API
    if (!allValues?.taxableTransactionDate) {
        updates.taxableTransactionDate = calculatedDueDate;
    }

    return updates;
};

// Create Decorator
const decorator = createDecorator(
    // Calculations:
    {
        field: /items\[\d+\]\.(price|vatRate)$/,
        updates: (value, name, allValues) => {

            const re = /items\[(\d+)\]\./;
            const itemIndex = parseInt(name.match(re)?.[1] as string);

            const vatField = `items[${itemIndex}].vat`;
            const priceWithVatField = `items[${itemIndex}].priceVat`;

            const price: number = parseInt(allValues?.["items"]?.[itemIndex]["price"] ?? 0);
            const vatRate: number = parseInt(allValues?.["items"]?.[itemIndex]["vatRate"] ?? 0);

            // Check if transferred VAT is enabled - if so, use 0% VAT rate for calculations
            const isTransferredVAT = allValues?.["transferredVAT"] || false;
            const effectiveVatRate = isTransferredVAT ? 0 : vatRate;

            // Round to whole numbers (integers)
            const priceWithVat = round(price * (1 + effectiveVatRate / 100), 0);
            const vat = round(price * (effectiveVatRate / 100), 0);

            // county totals
            const totals = {
                excludingVat: 0,
                vat: 0,
                includingVat: 0,
            };

            (allValues?.["items"] || [])
                .forEach(
                    (item) => {
                        const itemPrice = parseInt(item.price ?? 0);
                        // Use effective VAT rate (0 if transferred VAT is enabled)
                        const itemEffectiveVatRate = isTransferredVAT ? 0 : (item.vatRate ?? 0);
                        totals.excludingVat += itemPrice;
                        // Round individual item values to whole numbers, then sum
                        totals.vat += round(itemPrice * (itemEffectiveVatRate / 100), 0);
                        totals.includingVat += round(itemPrice * (1 + itemEffectiveVatRate / 100), 0);
                    }
                );

            // return modifications
            return {
                [priceWithVatField]: priceWithVat,
                [vatField]: vat,
                // Totals are already sums of rounded values, no need to round again
                price: totals.excludingVat,
                vat: totals.vat,
                priceVat: totals.includingVat,
            }
        }
    },
    {
        field: "transferredVAT",
        updates: (value, name, allValues) => {
            // When transferredVAT changes, recalculate all VAT amounts
            const isTransferredVAT = value || false;
            const updates = {};

            // Recalculate each item's VAT and priceVat
            (allValues?.["items"] || []).forEach((item, index) => {
                const price = parseInt(item.price ?? 0);
                const vatRate = parseInt(item.vatRate ?? 0);
                const effectiveVatRate = isTransferredVAT ? 0 : vatRate;

                // Round to whole numbers (integers)
                const vat = round(price * (effectiveVatRate / 100), 0);
                const priceWithVat = round(price * (1 + effectiveVatRate / 100), 0);

                updates[`items[${index}].vat`] = vat;
                updates[`items[${index}].priceVat`] = priceWithVat;
            });

            // Recalculate totals
            const totals = {
                excludingVat: 0,
                vat: 0,
                includingVat: 0,
            };

            (allValues?.["items"] || []).forEach((item) => {
                const itemPrice = parseInt(item.price ?? 0);
                const itemVatRate = parseInt(item.vatRate ?? 0);
                const itemEffectiveVatRate = isTransferredVAT ? 0 : itemVatRate;

                totals.excludingVat += itemPrice;
                // Round individual item values to whole numbers, then sum
                totals.vat += round(itemPrice * (itemEffectiveVatRate / 100), 0);
                totals.includingVat += round(itemPrice * (1 + itemEffectiveVatRate / 100), 0);
            });

            // Totals are already sums of rounded values, no need to round again
            updates["price"] = totals.excludingVat;
            updates["vat"] = totals.vat;
            updates["priceVat"] = totals.includingVat;

            return updates;
        }
    },
    {
        field: "currency",
        updates: (value, name, allValues) => {

            // const toField = name.replace('price', 'inInvoiceCurrency')


            const updates = {}


            return updates;
        }

    },
    {
        field: /(company|issueDate)/,
        updates: decorateDates //
    },
    {
        field: "company",
        updates: (value, name, allValues: any) => {
            // Only set transferredVAT from company if invoice doesn't already have it set

            let transferredVAT = allValues?.transferredVAT; // Keep existing invoice value

            // Only use company's transferredVAT if invoice doesn't have it set yet
            if (transferredVAT === undefined || transferredVAT === null) {
                transferredVAT = allValues?.company?.transferredVAT || false;
            }

            const updates: any = {
                transferredVAT: transferredVAT,
            };
            return updates;
        }
    },
    {
        field: "paymentMethodIsCash",
        updates: (value, name, allValues: any) => {
            // Convert boolean switch value to PaymentMethod enum
            const paymentMethod = value ? PaymentMethod.Cash : PaymentMethod.Transfer;
            return {
                paymentMethod: paymentMethod
            };
        }
    },
    {
        field: "paymentMethod",
        updates: (value, name, allValues: any) => {
            // Convert PaymentMethod enum to boolean for form display
            const paymentMethodIsCash = value === "Cash" || value === PaymentMethod.Cash;
            return {
                paymentMethodIsCash: paymentMethodIsCash
            };
        }
    },
    // {
    //     field:"issueDate",
    //     updates: decorateDates
    // }
);

const fields : ItemField<any>[] = [
    {
        name: "invoiceID",
        title: "ID",
        description: "ID",
        columnDef: {
            width: 50,
            ignore: true,
        },
        formFieldDef: {
            ignore: true
        }
    },
    {
        name: "number",
        title: "Faktura",
        columnDef: {
            width: 80,
        },
    },
    {
        name: "orderNumber",
        title: "Objednávka",
        columnDef: {
            minWidth: 90,
        },
    },
    {
        name: "offerNumber",
        title: "Nabídka",
        columnDef: {
            width: 80,
            valueGetter: (cell) => {
                const offer = cell.row.offer;
                const offerNumber = cell.value;
                const parts: string[] = [];

                // Add offer.number if it exists
                if (offer?.number) {
                    parts.push(offer.number);
                }

                // Only add offerNumber if it's different from offer.number
                if (offerNumber && (!offer?.number || offer.number !== offerNumber)) {
                    parts.push(offerNumber);
                }

                return parts.join(" ");
            },
            renderCell: (cell) => {
                const offer = cell.row.offer;
                const offerNumber = cell.row.offerNumber; // Get original field value from row data

                const hasOfferLink = offer?.offerID && offer?.number;
                const hasOfferNumber = offerNumber;

                // Check if both values are the same - if so, prefer the linked version
                const areNumbersSame = hasOfferLink && hasOfferNumber && offer.number === offerNumber;
                const shouldShowOfferNumber = hasOfferNumber && !areNumbersSame;

                if (!hasOfferLink && !hasOfferNumber) {
                    return <Box>-</Box>;
                }

                return (
                    <Box>
                        {hasOfferLink && (
                            <Box style={{display: 'block', marginBottom: shouldShowOfferNumber ? '4px' : '0'}}>
                                <MuiLink
                                    component={Link}
                                    to={`/offers/offers/${offer.offerID}`}
                                    color="primary"
                                    underline="hover"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {offer.number}
                                </MuiLink>
                            </Box>
                        )}
                        {shouldShowOfferNumber && (
                            <Box style={{display: 'block', color: '#666', fontSize: '0.875rem'}}>
                                {offerNumber}
                            </Box>
                        )}
                    </Box>
                );
            },
        },
    },
    {
        name: "issueDate",
        title: "Vystavení",
        columnDef: {
            width: 90,
            type: "date",
            valueFormatter: dateFormatter
        },
        formFieldDef: {
            type: "date"
        }
    },
    {
        name: "taxableTransactionDate",
        title: "Datum zdanitelného plnění",
        columnDef: {
            flex: 0,
            type: "date",
            valueFormatter: dateFormatter,
            hide: true,
        },
        formFieldDef: {
            type: "date"
        }
    },
    {
        name: "dueDate",
        title: "Splatnost",
        columnDef: {
            width: 90,
            type: "date",
            valueFormatter: dateFormatter
        },
        formFieldDef: {
            type: "date"
        }
    },
    {
        name: "company",
        title: "Firma",
        columnDef: {
            flex: 2,
            minWidth: 150,
            valueGetter: (cell) => {
                return cell.value?.name
            },
            // valueFormatter: (params) => {
            //     return params.value.name;
            // }
        },
    },
    {
        name: "total",
        title: "Cena",
        columnDef: {
            width: 90,
            type: "number",
            renderCell: ({ value }) => formatNumberThousands(formatNumber(value)),
            valueFormatter: ({ value }) => value,
        },
        // formFieldDef: {
        //     type: "date"
        // }
    },
    {
        name: "priceVat",
        title: "+ DPH",
        columnDef: {
            width: 90,
            type: "number",
            renderCell: ({ value }) => formatNumberThousands(formatNumber(value)),
            valueFormatter: ({ value }) => value,
        },
    },
    {
        name: "currency",
        title: "Měna",
        columnDef: {
            flex: 0,
            hide: true,
            valueGetter: (params) => {
                return params.value?.alpha3Code;
            }
        },
    },
    {
        name: "items",
        title: "Položky",
        columnDef: {
            flex: 3,
            minWidth: 200,
            valueGetter: (cell) => {
                return cell.value?.map(item => `${item.title} ${item.description}`)?.join(" ")
            },
            renderCell:(cell) => {
                return <Grid container direction={"row"}>
                    {cell.row.items?.map(item => (
                        <>
                            <Grid item xs={12}>
                                {item.title}
                            </Grid>
                            <Grid item xs={12}>
                                &nbsp;-&nbsp;{item.description}
                            </Grid>
                        </>
                    ))}
                </Grid>
            },
        },
        formFieldDef: {
            render: (field, classes, form, readonly) => (
                <InvoiceItemsFormPart field={field} readonly={readonly} form={form} />
            )
        }
    },
    {
        name: "note",
        title: "Poznámka",
        columnDef: {
            minWidth: 150,
            flex: 2,
        },
    },
    {
        name: "invoiceState",
        title: "Stav",
        columnDef: {
            width: 80,
            valueGetter: (params) => {
                return normalizeInvoiceStateLabel(params.value);
            },
            renderCell: (cell) => (
                <Box style={{fontWeight: cell.value == InvoiceState.Paid?'bold':'normal'}}>{normalizeInvoiceStateLabel(cell.value)}</Box>
            )
        },
    },
    {
        name: "paymentStatus",
        title: "Stav zaplacení",
        columnDef: {
            flex: 0,
            width: 130,
            valueGetter: (params) => {
                return getPaymentStatus(params.row.invoiceState, params.row.dueDate);
            },
            renderCell: (cell) => {
                const status = getPaymentStatus(cell.row.invoiceState, cell.row.dueDate);
                const color = getPaymentStatusColor(status);
                return (
                    <Chip
                        label={status}
                        size="small"
                        style={{
                            backgroundColor: color,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                        }}
                    />
                );
            }
        },
        formFieldDef: {
            ignore: true
        }
    },

    {
        name: "companyID",
        title: "ID firmy",
        columnDef: {
            flex: 2,
            hide: true,
            ignore: true,
        },
        formFieldDef: {
            render: (field, classes, form, readonly) => (
                <InputSelect field={field} readonly={readonly} itemsGetter={async (search) => {
                    const companies: CompanyResponse[] = await getAllCompanies();
                    return companies.map(c => (
                        <MenuItem value={c.companyID}>{c.name}</MenuItem>
                    ))
                }} />
            )
        }
    },
    {
        name: "currencyID",
        title: "ID Měny",
        columnDef: {
            flex: 1,
            hide: true,
            ignore: true,
        },
        formFieldDef: {
            render: (field, classes, form, readonly) => (
                <InputSelect field={field} readonly={readonly} itemsGetter={async (search) => {
                    const currencies: CurrencyResponse[] = await getAllCurrencies();
                    return currencies.map(c => (
                        <MenuItem value={c.currencyID}>{c.name}</MenuItem>
                    ))
                }} />
            )
        }
    },
    {
        name: "exchangeRate",
        title: "Kurz",
        columnDef: {
            flex: 1,
            hide: true,
            ignore: true,
        },
        formFieldDef: {
            ignore: true
        }
    },
    {
        name: "transferredVAT",
        title: "Přenesená DPH",
        description: "Přenesená DPH",
        formFieldDef: {
            render: (field, classes, form, readonly) => {
                return (
                    <Switches
                        {...field}
                        name={field.name}
                        data={{label: field.title, value: true}}
                    />
                );
            },
        },
        columnDef: {
            flex: 2,
            hide: true,
            renderCell: params => {
                return params.value ? "Ano" : "Ne";
            }
        },
    },
    {
        name: "paymentMethodIsCash",
        title: "Způsob platby",
        description: "Způsob platby",
        formFieldDef: {
            render: (field, classes, form, readonly) => {
                return (
                    <Switches
                        {...field}
                        name={field.name}
                        data={{label: "Hotově", value: true}}
                        helperText="Zaškrtnut = Hotově, Nezaškrtnut = Převodem"
                    />
                );
            },
        },
        columnDef: {
            flex: 1,
            hide: true,
            renderCell: params => {
                return params.value ? "Hotově" : "Převodem";
            }
        },
    },
];

const InvoiceModule: React.FC = () => {
    return (
        // <CrudderProvider>
            <InvoiceModuleInner />
        // </CrudderProvider>
        )
}

const InvoiceModuleInner: React.FC = () => {

    const [ data, setData ] = useState<InvoiceResponse[]>([]);

    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    const offerIDParam = searchParams.get("offerID");

    // const { detailResponse,  } = useInvoiceContext();

    const { keycloak, initialized: keycloakInitialized } = useKeycloak();

    let offerID = useMemo(() => {
        if (offerIDParam) {
            const id = parseInt(offerIDParam);
            return isNaN(id) ? undefined : id;
        }
        return undefined;
    }, [offerIDParam]);


    const fetchData = useCallback(() => {
        return new Promise<InvoiceResponse[]>((resolve, reject) => {
            getAllInvoices().then((data) => {
                setData(data);
                resolve(data)
            })
        });
    }, []);

    const handleSubmit = useCallback((formValues: FormData) => {
        return new Promise<OnSubmitDone>((resolve, reject) => {
            // Normalize date fields to end of day before sending to API
            const normalizedValues = { ...formValues };

            // Normalize dueDate to end of day (23:59:59.999)
            if (normalizedValues["dueDate"]) {
                const dueDate = new Date(normalizedValues["dueDate"] as any);
                dueDate.setHours(23, 59, 59, 999);
                normalizedValues["dueDate"] = dueDate;
            }

            // Normalize taxableTransactionDate to end of day (23:59:59.999)
            if (normalizedValues["taxableTransactionDate"]) {
                const taxableDate = new Date(normalizedValues["taxableTransactionDate"] as any);
                taxableDate.setHours(23, 59, 59, 999);
                normalizedValues["taxableTransactionDate"] = taxableDate;
            }

            // Convert paymentMethodIsCash boolean to PaymentMethod enum
            if (normalizedValues["paymentMethodIsCash"] !== undefined) {
                normalizedValues["paymentMethod"] = normalizedValues["paymentMethodIsCash"] ? PaymentMethod.Cash : PaymentMethod.Transfer;
                delete normalizedValues["paymentMethodIsCash"];
            }

            if (formValues["invoiceID"]) {
                // update
                // @ts-ignore
                updateInvoice(formValues["invoiceID"], normalizedValues as InvoiceRequest).then((response) => {
                    resolve({id: response.invoiceID});
                })
            }
            else {
                // create
                // @ts-ignore
                addInvoice(normalizedValues as InvoiceRequest).then((response) => {
                    resolve({id: response.invoiceID, inserted: true });
                })
            }

        })
    }, []);

    const handleDelete: (values: number) => Promise<void> = useCallback((id: number) => {
        return new Promise<void>((resolve, reject) => {
            deleteInvoice(id).then((response) => {
                resolve();
            })
        });
    }, []);

    const getDetail = useCallback((id) => {
        return new Promise<InvoiceResponse>((resolve, reject) => {
            getInvoiceDetails(id).then((data) => {
                // Transform PaymentMethod enum to boolean for form
                const transformedData = {
                    ...data,
                    paymentMethodIsCash: data.paymentMethod === PaymentMethod.Cash || data.paymentMethod?.toString() === "Cash"
                };
                resolve(transformedData)
            })
        });
    }, [data]);

    const getDefaultData = useCallback(() => {
        return new Promise<InvoiceResponse>((resolve, reject) => {
            getInitialData(offerID).then((data) => {
                // Transform PaymentMethod enum to boolean for form
                const transformedData = {
                    ...data,
                    paymentMethodIsCash: data.paymentMethod === PaymentMethod.Cash || data.paymentMethod?.toString() === "Cash"
                };
                resolve(transformedData)
            })
        });
    }, [data]);


    return ( keycloakInitialized && keycloak?.token ? (
            <CrudModule<InvoiceResponse, InvoiceRequest>
                name={"Faktury"}
                list={{
                    title: "Seznam faktur",
                    getRowID: (currency) => currency.invoiceID,
                    hideHeader: true,
                    initialState: {
                        sorting: {
                            sortModel: [{ field: 'number', sort: 'desc' }],
                        },
                    },
                    actionsHidden: true,
                }}
                detail={{
                    title: "Detail faktury",
                    formTemplate: InvoiceForm as any,
                    decorators: [decorator as any],
                    mutators: {
                        setCurrency: ([ currency ], state, utils) => {
                            utils.changeValue(state, 'currency', () => currency)
                        },
                        setCompany: ([ company ], state, utils) => {
                            utils.changeValue(state, 'company', () => company);
                            // @ts-ignore
                            const currency = state.formState.values.currencyID;
                            if (state.formState.initialValues?.["currencyID"] == currency && company?.currencyID) {
                                // currency not set yet
                                utils.changeValue(state, 'currencyID', () => company?.currencyID);
                            }
                            // @ts-ignore
                            const transferredVAT = state.formState.values.transferredVAT;
                            if (state.formState.initialValues?.["transferredVAT"] == transferredVAT && company?.transferredVAT !== undefined) {
                                // transferredVAT not set yet, copy from company
                                utils.changeValue(state, 'transferredVAT', () => company?.transferredVAT);
                            }
                            // @ts-ignore
                            const issueDate = state.formState.values.issueDate;
                            if (issueDate) {
                                // Recalculate dueDate based on company settings
                                const invoiceDue = company?.invoiceDue || 14; // Default 14 days
                                const date = moment(issueDate).toDate();
                                const calculatedDueDate = date ? new Date(date.getTime() + invoiceDue * 24 * 60 * 60 * 1000) : undefined;
                                if (calculatedDueDate) {
                                    utils.changeValue(state, 'dueDate', () => calculatedDueDate);
                                }
                            }
                        },
                        initializePaymentMethod: ([], state, utils) => {
                            // Convert PaymentMethod enum to boolean for form when initializing
                            const paymentMethod = (state.formState.values as any).paymentMethod;
                            if (paymentMethod !== undefined) {
                                const paymentMethodIsCash = paymentMethod === "Cash" || paymentMethod === PaymentMethod.Cash;
                                utils.changeValue(state, 'paymentMethodIsCash', () => paymentMethodIsCash);
                            }
                        },
                    }
                }}
                itemFields={fields}
                data={fetchData}
                getDetail={getDetail as any}
                getDefaultData={getDefaultData as any}
                onDelete={handleDelete}
                onSubmit={handleSubmit}
            />
    ) : null

    );
};

export default InvoiceModule;